import { useDispatch, useSelector } from "react-redux";
import { clsx } from "clsx";
import { FullPageContainer } from "../../common/FullPageContainer/FullPageContainer";
import { OccupancyRateBarLineCharts } from "../../common/OccupancyRateCharts/OccupancyRateBarLineCharts";
import { PerfomanceHighlights } from "./components/PerfomanceHighlights";
import { isMobileModeSelector } from "../../../redux/selectors/appStatusSelector";
import { DailyOccupancyChart } from "../OccupancyPage/components/DailyOccupancyChart/DailyOccupancyChart";
import { OccupancyRateVsMarket } from "../OccupancyPage/components/OccupancyRateVsMarket/OccupancyRateVsMarket";
import classes from "./PerfomancePage.module.scss";
import { RevenueTable } from "../RevenuePage/components/RevenueTable/RevenueTable";
import { RevenueByMonth } from "../RevenuePage/components/RevenueByMonth/RevenueByMonth";
import { ReservationsByChannelPie } from "../RevenuePage/components/ReservationsByChannelPie/ReservationsByChannelPie";
import { RevenuePerNightCard } from "../RevenuePage/components/RevenueCards/RevenuePerNight/RevenuePerNightCard";
import { periodList } from "../../../utils/constants/periodLists";
import { PeriodDropdown } from "../../common/PeriodDropdown/PeriodDropdown";
import {
  getSelectedListingsSelector,
  performancePagePeriodSelector,
} from "../../../redux/selectors/performancePageSelector";
import { SET_PERFORMANCE_PAGE_PERIOD } from "../../../redux/actions";
import { Typography } from "../../../shared/ui/typography";
import { useEffect, useMemo } from "react";
import { IListing } from "@/views/interfaces";
import { PERFORMANCE_PAGE_ACTIONS_TO_DISPATCH } from "@/utils/constants/performancePageActions";
import { userDataSelector } from "@/redux/selectors/userDataSelector";

export const PerfomancePage = () => {
  const dispatch = useDispatch();
  const isMobileView = useSelector(isMobileModeSelector);
  const period = useSelector(performancePagePeriodSelector);
  const listings: IListing[] = useSelector(getSelectedListingsSelector);
  const source = useSelector(userDataSelector)?.source;

  const newPeroidList = periodList.map((period) =>
  period.id.toLowerCase() === 'last 12 months'
    ? { ...period, label: 'Year to date' }
    : period
  );

  const listingsIds = useMemo(() => {
    return (listings !== null ? listings : []).map(
      (listing) => listing.property_id
    );
  }, [listings]);

  const setPeriod = (period: string) => {
    dispatch({
      type: SET_PERFORMANCE_PAGE_PERIOD,
      payload: { period },
    });
  };

  useEffect(() => {
    PERFORMANCE_PAGE_ACTIONS_TO_DISPATCH.forEach((action) => {
      dispatch({
        type: action,
        payload: listingsIds,
      });
    });
  }, [dispatch, listingsIds]);

  return (
    <FullPageContainer className={classes.wrapper}>
      <>
        <div className={classes.highlightsHeader}>
          <Typography
            className={classes.gray900}
            variant={isMobileView ? "display-xs" : "display-md"}
            weight="semibold"
          >
            Performance
          </Typography>
          <PeriodDropdown
            className={classes.outlinedDropdown}
            period={period}
            periodList={newPeroidList}
            setPeriod={setPeriod}
          />
        </div>
        <PerfomanceHighlights />
        <Typography
          className={clsx(classes.subTitle)}
          variant={"text-lg"}
          weight="medium"
        >
          Occupancy metrics <br />
          <p><strong style={{ fontSize: "13px" }}>Notice on Occupancy Rate Metrics</strong><br /><small>
          We are currently experiencing issues with the Occupancy Rate metrics displayed on the dashboard. 
          Our team is actively working on correcting this as quickly as possible. We appreciate your patience and understanding, 
          and we encourage you to reach out to your Account Manager if you have any questions or concerns.</small>
          </p>
        </Typography>
        {isMobileView ? (
          <>
            <OccupancyRateVsMarket />
            <DailyOccupancyChart />
            <OccupancyRateBarLineCharts page="performance" bothCharts />
            <Typography
              className={clsx(classes.subTitle)}
              variant={"text-lg"}
              weight="medium"
            >
              Revenue metrics
            </Typography>
            <RevenueByMonth />
            <RevenueTable />
            {source !== 'pricelabs' ? (
              <>
                <ReservationsByChannelPie />
                <RevenuePerNightCard />
              </>
            ) : (               
              <RevenuePerNightCard />             
            )}           
          </>
        ) : (
          <>
            <div className={classes.fullWidthBlock}>
              <OccupancyRateVsMarket />
              <DailyOccupancyChart />
            </div>
            <OccupancyRateBarLineCharts page="performance" bothCharts />
            <Typography
              className={clsx(classes.subTitle)}
              variant={"text-lg"}
              weight="medium"
            >
              Revenue metrics
            </Typography>
            <RevenueByMonth />
            {source !== 'pricelabs' ? (
              <>
                <div className={classes.block}>
                  <RevenueTable />
                  <ReservationsByChannelPie />
                </div>
                <div
                  className={clsx(
                    classes.fullWidthBlock,
                    classes.revenuePerNightCard
                  )}
                >
                  <RevenuePerNightCard />
                </div>
              </>
            ) : (
              <div className={classes.block}>
                <RevenueTable />
                <RevenuePerNightCard />
              </div>
            )}            
          </>
        )}
      </>
    </FullPageContainer>
  );
};
